import React, {
  DetailedHTMLProps,
  ButtonHTMLAttributes,
  forwardRef,
  ReactElement,
  cloneElement,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';

import styles from './index.module.css';

export type RSIconButtonProps = DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  icon: ReactElement;
};

export const RSIconButton = forwardRef<HTMLButtonElement, RSIconButtonProps>(
  ({ icon, ...props }, ref) => {
    const disabled = props.disabled;
    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';

    // propsとして渡すiconのviewBoxなどを指定しなくてもcloneして固定する
    const clonedIcon = cloneElement(icon, {
      className: 'block group-disabled:fill-gray-200',
      // cloneElementを用いるとstyled-jsxが利用できないため
      style: { fill: !disabled ? color : undefined },
      width: 32,
      height: 32,
      viewBox: '0 0 48 48',
    });

    return (
      <>
        <div
          className={classnames(
            'wrapper',
            isFocusVisible && 'focus-visible',
            'inline-block',
            'relative',
            'h-56',
            'w-56',
            'rounded-15',
            'after:transition',
          )}
        >
          <button
            {...mergeProps(props, focusProps)}
            ref={ref}
            className={classnames(
              'group',
              'box-border',
              'h-full',
              'w-full',
              'relative',
              'border',
              'border-solid',
              'border-gray-200',
              'disabled:border-gray-10',
              'bg-white',
              'disabled:bg-gray-10',
              'text-blue-700',
              'disabled:text-gray-200',
              'rounded-15',
              'flex',
              'justify-center',
              'items-center',
              'outline-none',
              'focus:outline-none',
              'z-1',
              'transition',
              'cursor-pointer',
              'disabled:cursor-not-allowed',
              !disabled && styles['button-default'],
            )}
          >
            {clonedIcon}
          </button>
        </div>
        <style jsx>{`
          .wrapper::after {
            content: '';
            box-sizing: border-box;
            position: absolute;
            width: calc(100% + 8px);
            height: calc(100% + 8px);
            top: -4px;
            left: -4px;
            border-style: solid;
            border-width: 2px;
            border-color: transparent;
            border-radius: 18px;
          }

          .wrapper.focus-visible::after {
            border-color: ${color};
          }
        `}</style>
      </>
    );
  },
);

RSIconButton.displayName = 'RSIconButton';
